/**
 * Tiered account recovery (`account-security`): how a recovery
 * request, a pending sign-in approval and the reset-link requirement read,
 * and which actions a viewer is offered. The server enforces every rule
 * (tiers, two-person rule, holds); these only keep the UI from offering a
 * button the server would refuse.
 */

import type {
  IdentityCheckStatus,
  LoginMfaMethod,
  SessionApproval,
  RecoveryApprovalRole,
  RecoveryErrorCode,
  RecoveryMissing,
  RecoveryRequest,
  RecoveryRequestType,
  RecoveryStatus,
  SecurityStatus,
} from './domain';
import { contactPayloadValue, validateContactValue } from './validation';

/** Presentation-neutral tone, structurally compatible with `@dealr/ng-ui`'s `DealrStatusTone`. */
export type RecoveryTone = 'success' | 'danger' | 'warning' | 'info' | 'neutral';

// ── Status fields ───────────────────────────────────────────────────────────

/** The server sends `tiered_recovery: true` only while the flag is on. */
export function tieredRecoveryOn(status: Pick<SecurityStatus, 'tiered_recovery'> | null | undefined): boolean {
  return !!status && status.tiered_recovery === true;
}

/** Shown to dealership admins when fewer than two people could recover each other's accounts. */
export function showSingleAdminWarning(status: Pick<SecurityStatus, 'tiered_recovery' | 'dealership_admin_count'> | null | undefined): boolean {
  return tieredRecoveryOn(status) && typeof status!.dealership_admin_count === 'number' && status!.dealership_admin_count < 2;
}

/** `account-security`: the count is sent whenever approvals exist; no tiered flag needed. */
export function pendingSessionApprovalCount(status: Pick<SecurityStatus, 'pending_session_approvals'> | null | undefined): number {
  if (!status) return 0;
  const count = status.pending_session_approvals;
  return typeof count === 'number' && count > 0 ? count : 0;
}

// ── Login: approve from another signed-in device ───────────────────────────

/** The `method_uuid` the login step lists "Approve from another signed-in device" under. */
export const SESSION_APPROVAL_METHOD_UUID = 'session';
/** The server's prompt lives five minutes; the poller stops waiting at the same point. */
export const SESSION_APPROVAL_TIMEOUT_MS = 5 * 60 * 1000;

export function isSessionApprovalMethod(method: Pick<LoginMfaMethod, 'type' | 'uuid'> | null | undefined): boolean {
  return !!method && (method.type === 'session_approval' || method.uuid === SESSION_APPROVAL_METHOD_UUID);
}

/** `/login/mfa/verify` answers 202 `{pending:true}` while the other session has not decided. */
export function loginVerifyPending(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as Record<string, unknown>)['pending'] === true;
}

/** Wait before poll `attempt` (0-based): 2s, 3s, 4.5s, … capped at 10s. */
export function sessionApprovalPollDelay(attempt: number): number {
  return Math.min(10000, Math.round(2000 * Math.pow(1.5, Math.max(0, attempt))));
}

// ── Number matching and request kinds ───────────────────────────────────────

/** What the asking screen hears once the other device denied (or a wrong number was picked). */
export const SESSION_APPROVAL_DENIED = 'This sign-in was denied on your other device.';
export const STEP_UP_APPROVAL_DENIED = 'This was denied on your other device.';
/** The approver's answer to a wrong pick (the server's 403 `wrong_number`). */
export const SESSION_APPROVAL_WRONG_NUMBER = 'That wasn’t the number on the sign-in screen, so this request was denied.';

/** The step-up actions the server names in its prompt ("… wants to change your password."). */
export type StepUpApprovalAction = 'change_password' | 'add_method' | 'remove_method' | 'backup_codes' | 'verify_identity' | 'name_change';

const ACTION_WORDS: Record<StepUpApprovalAction, string> = {
  change_password: 'change your password',
  add_method: 'add a sign-in method',
  remove_method: 'remove a sign-in method',
  backup_codes: 'create new backup codes',
  verify_identity: 'verify your identity',
  name_change: 'request a legal name change',
};

const isMatchNumber = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 10 && n <= 99;

/** A challenge reply's `match_number` (10-99), or null when the server sent none. */
export function approvalMatchNumber(reply: unknown): number | null {
  const n = typeof reply === 'object' && reply !== null ? (reply as Record<string, unknown>)['match_number'] : undefined;
  return isMatchNumber(n) ? n : null;
}

/** The waiting screen's line: "Pick 47 on your other device", or the plain ask without a number. */
export function matchNumberPrompt(matchNumber: number | null, kind: SessionApprovalKindLike = 'sign_in'): string {
  if (matchNumber !== null) return `Pick ${matchNumber} on your other device`;
  return kind === 'step_up' ? 'Approve this on a device where you’re signed in.' : 'Approve this sign-in on your other device.';
}

type SessionApprovalKindLike = 'sign_in' | 'step_up' | string | undefined;

/** A pending prompt's numbers to pick from: valid two-digit numbers, no repeats, in the server's order. */
export function sessionApprovalChoices(approval: Pick<SessionApproval, 'choices'> | null | undefined): number[] {
  const out: number[] = [];
  for (const n of approval?.choices ?? []) if (isMatchNumber(n) && !out.includes(n)) out.push(n);
  return out;
}

/** "Sign-in request", or "Confirm it’s you" for a step-up. */
export function sessionApprovalTitle(kind: SessionApprovalKindLike): string {
  return kind === 'step_up' ? 'Confirm it’s you' : 'Sign-in request';
}

/** What the prompt asks: "is signing in to your account" or a step-up's change. */
export function sessionApprovalAsk(approval: Pick<SessionApproval, 'kind' | 'action'>): string {
  if (approval.kind !== 'step_up') return 'is signing in to your account';
  const words = approval.action ? (ACTION_WORDS as Record<string, string>)[approval.action] : undefined;
  return words ? `wants to ${words}` : 'wants to confirm it’s you for a change to your account';
}

// ── Recovery requests ───────────────────────────────────────────────────────

const TYPE_LABELS: Record<RecoveryRequestType, string> = {
  mfa_reset: 'Two-step verification reset',
  email_change: 'Email change',
  legal_name_change: 'Legal name change',
  phone_change: 'Cell phone change',
};

export function recoveryTypeLabel(type: RecoveryRequestType | string): string {
  return (TYPE_LABELS as Record<string, string>)[type] ?? type;
}

const STATUS: Record<RecoveryStatus, { label: string; tone: RecoveryTone }> = {
  pending: { label: 'Waiting for approval', tone: 'warning' },
  on_hold: { label: 'On 24-hour hold', tone: 'info' },
  completed: { label: 'Completed', tone: 'success' },
  denied: { label: 'Denied', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  frozen: { label: 'Frozen by the user', tone: 'danger' },
  expired: { label: 'Expired', tone: 'neutral' },
};

export function recoveryStatusLabel(status: RecoveryStatus | string): string {
  return (STATUS as Record<string, { label: string }>)[status]?.label ?? status;
}

export function recoveryStatusTone(status: RecoveryStatus | string): RecoveryTone {
  return (STATUS as Record<string, { tone: RecoveryTone }>)[status]?.tone ?? 'neutral';
}

export function recoveryTierLabel(tier: number): string {
  return tier === 3 ? 'Tier 3 · high risk' : `Tier ${tier}`;
}

export function recoveryIsOpen(request: Pick<RecoveryRequest, 'status'>): boolean {
  return request.status === 'pending' || request.status === 'on_hold';
}

const IDENTITY: Record<IdentityCheckStatus, { label: string; tone: RecoveryTone }> = {
  not_required: { label: 'Not required', tone: 'neutral' },
  required: { label: 'Not started', tone: 'warning' },
  pending: { label: 'Waiting for the user', tone: 'info' },
  passed: { label: 'Passed', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  expired: { label: 'Expired', tone: 'warning' },
  declined: { label: 'Declined by the user', tone: 'danger' },
  unavailable: { label: 'ID check unavailable', tone: 'neutral' },
  alternative: { label: 'Confirmed by a dealership admin instead', tone: 'success' },
};

export function identityCheckLabel(status: IdentityCheckStatus | string): string {
  return (IDENTITY as Record<string, { label: string }>)[status]?.label ?? status;
}

export function identityCheckTone(status: IdentityCheckStatus | string): RecoveryTone {
  return (IDENTITY as Record<string, { tone: RecoveryTone }>)[status]?.tone ?? 'neutral';
}

export const IDENTITY_CHECK_UNAVAILABLE_MESSAGE = 'ID check not available. Contact Dealr support.';

const MISSING_LABELS: Record<RecoveryMissing, string> = {
  dealer_admin: 'Dealership admin approval',
  identity_check: 'ID and face check by the user, or a dealership admin’s identity confirmation',
  second_approval: 'Second approval (dealership admin or Dealr callback)',
  supervisor: 'Dealr supervisor approval',
};

export function missingLabel(missing: RecoveryMissing | string): string {
  return (MISSING_LABELS as Record<string, string>)[missing] ?? missing;
}

/** Approvals a person still has to give; the ID check is the user's own step, not an approval. */
export function approvalsRemaining(request: Pick<RecoveryRequest, 'missing'>): number {
  return (request.missing || []).filter((missing) => missing !== 'identity_check').length;
}

/**
 * A bare `Y-m-d H:i:s` server timestamp read as browser-local time, the
 * convention every Dealr date display uses. Null when empty or unparseable.
 */
export function parseRecoveryTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const at = Date.parse(/^\d{4}-\d{2}-\d{2} \d/.test(value) ? value.replace(' ', 'T') : value);
  return Number.isNaN(at) ? null : at;
}

/** Milliseconds left on the 24-hour hold, or null when the request is not on hold. */
export function holdRemainingMs(request: Pick<RecoveryRequest, 'status' | 'hold_until'>, now: number): number | null {
  if (request.status !== 'on_hold') return null;
  const until = parseRecoveryTime(request.hold_until);
  if (until === null) return null;
  return Math.max(0, until - now);
}

/** "23h 05m left", "4m left", or the moment the worker picks it up. */
export function formatHoldCountdown(ms: number | null): string {
  if (ms === null) return '';
  if (ms <= 0) return 'Hold ended; applies within 10 minutes';
  const minutes = Math.ceil(ms / 60000);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m left`;
  return `${hours}h ${rest < 10 ? '0' : ''}${rest}m left`;
}

export interface RecoveryViewer {
  userId: number | null;
  /** Signed in as Dealr staff (admin shell, or a Dealr admin in a dealership). */
  isDealrStaff: boolean;
  /** Has `admin.security_recovery_approve`. */
  canSupervise: boolean;
  /** An authorized contact at the dealership; only they may confirm identity without an ID check. */
  isAuthorizedContact?: boolean;
}

export interface RecoveryActions {
  approveAs: RecoveryApprovalRole[];
  canDeny: boolean;
  canCancel: boolean;
  canSendIdentityCheck: boolean;
  /** A dealership admin can confirm the user's identity instead of the ID check. */
  canConfirmIdentity: boolean;
}

/**
 * The buttons a viewer gets. Mirrors the two-person rule (target, initiator
 * and each approver are different people) and who may give which approval.
 */
export function recoveryActions(request: RecoveryRequest, viewer: RecoveryViewer): RecoveryActions {
  const none: RecoveryActions = { approveAs: [], canDeny: false, canCancel: false, canSendIdentityCheck: false, canConfirmIdentity: false };
  if (!recoveryIsOpen(request)) return none;
  const me = viewer.userId;
  const isInitiator = me !== null && me === request.initiator_user_id;
  const isTarget = me !== null && me === request.user_id;
  const approvals = request.approvals || { dealer_admin: null, callback: null, supervisor: null };
  const alreadyApproved =
    me !== null &&
    [request.identity_confirmation, approvals.dealer_admin, approvals.callback, approvals.supervisor].some((a) => !!a && a.user_id === me);
  const missing = request.missing || [];
  const approveAs: RecoveryApprovalRole[] = [];
  if (!isInitiator && !isTarget && !alreadyApproved) {
    if (viewer.isDealrStaff) {
      if (request.tier === 3 && missing.indexOf('second_approval') !== -1) approveAs.push('callback');
      if (request.tier === 3 && viewer.canSupervise && missing.indexOf('supervisor') !== -1) approveAs.push('supervisor');
    } else if (missing.indexOf('dealer_admin') !== -1 || missing.indexOf('second_approval') !== -1) {
      approveAs.push('dealer_admin');
    }
  }
  return {
    approveAs,
    canDeny: !isTarget && request.status === 'pending',
    canCancel: isInitiator,
    canSendIdentityCheck:
      missing.indexOf('identity_check') !== -1 && request.identity_check !== 'pending' && request.identity_check !== 'passed',
    canConfirmIdentity:
      !viewer.isDealrStaff &&
      !!viewer.isAuthorizedContact &&
      !isInitiator &&
      !isTarget &&
      !alreadyApproved &&
      request.tier === 3 &&
      request.type !== 'legal_name_change' &&
      missing.indexOf('identity_check') !== -1,
  };
}

const ROLE_LABELS: Record<RecoveryApprovalRole, string> = {
  dealer_admin: 'Approve as dealership admin',
  callback: 'Record callback approval',
  supervisor: 'Give supervisor approval',
};

export function approvalRoleLabel(role: RecoveryApprovalRole | string): string {
  return (ROLE_LABELS as Record<string, string>)[role] ?? role;
}

/** The server requires a callback note of at least this length. */
export const CALLBACK_NOTE_MIN = 10;

export function validateCallbackNote(note: string): string | null {
  return (note || '').trim().length >= CALLBACK_NOTE_MIN
    ? null
    : `Describe the callback in at least ${CALLBACK_NOTE_MIN} characters: who you spoke with, which number on file you called, and what they confirmed.`;
}

/** The server requires an identity-confirmation note of at least this length. */
export const IDENTITY_CONFIRMATION_NOTE_MIN = 10;

export function validateIdentityConfirmationNote(note: string): string | null {
  return (note || '').trim().length >= IDENTITY_CONFIRMATION_NOTE_MIN
    ? null
    : `Describe in at least ${IDENTITY_CONFIRMATION_NOTE_MIN} characters how you confirmed who they are: for example, in person with their photo ID, or on a video call.`;
}

/** Validates a new recovery request and returns the body fields to send (without `step_up`). */
export function recoveryRequestDraft(input: { type: RecoveryRequestType; value?: string; reason?: string }):
  | { error: string }
  | { body: { type: RecoveryRequestType; value?: string; reason?: string } } {
  const body: { type: RecoveryRequestType; value?: string; reason?: string } = { type: input.type };
  if (input.type === 'email_change' || input.type === 'phone_change') {
    const field = input.type === 'email_change' ? 'email' : 'phone';
    const invalid = validateContactValue(field, input.value || '');
    if (invalid) return { error: invalid };
    body.value = contactPayloadValue(field, input.value || '');
  }
  const reason = (input.reason || '').trim();
  if (reason) body.reason = reason;
  return { body };
}

const ERROR_MESSAGES: Record<RecoveryErrorCode, string> = {
  security_lead_required:
    "This account can't be recovered here: it is the dealership's only admin or a Dealr staff account. Contact the Dealr security lead.",
  recent_recovery: 'An email/phone change and a two-step reset cannot happen within 24 hours of each other. Try again later.',
  requires_recovery_request: 'This change needs a recovery request with additional approvals.',
  two_person_rule: 'A different person must approve. The user, whoever started the request, and each approver must all be different people.',
  identity_check_unavailable: IDENTITY_CHECK_UNAVAILABLE_MESSAGE,
  post_recovery_step_up_required: 'This account was recovered recently. Confirm it’s you before changing users or permissions.',
  no_verified_identity: 'A legal name change needs a verified identity first. Verify your identity in your security settings.',
  identity_enrollment_unavailable: 'Identity verification isn’t available for this account.',
};

/** A friendly message for a tiered error code, or null for an unknown code. */
export function recoveryErrorMessage(code: string | null | undefined): string | null {
  return code ? ((ERROR_MESSAGES as Record<string, string>)[code] ?? null) : null;
}

// ── Reset link (self-service) ───────────────────────────────────────────────

/** What the reset page shows for each `identity_check` state; `passed` unlocks the password form. */
export function resetIdentityCheckMessage(status: string | null | undefined): string {
  switch (status) {
    case 'not_started':
    case 'required':
      return 'Before you choose a new password, confirm your identity with a photo ID and a quick face check.';
    case 'pending':
      return 'We sent you a link to check your ID. Finish it, then come back here and continue.';
    case 'passed':
      return 'Identity confirmed.';
    case 'failed':
    case 'declined':
    case 'expired':
      return 'The ID check didn’t pass on this link. Request a new reset link to try again with a fresh check.';
    default:
      return 'We couldn’t confirm your ID check. Request a new reset link to try again.';
  }
}
